import { 
  collection, 
  doc, 
  getDoc, 
  updateDoc, 
  increment, 
  getDocs, 
  query, 
  where, 
  setDoc,
  addDoc,
} from "firebase/firestore";
import { firestore } from "../firebase/firebaseConfig";

/**
 * Fetches details of a specific user including KYC status.
 * @param {string} userId - The unique ID of the user.
 * @returns {Promise<Object|null>} The user object or null if not found.
 */
export const fetchUser = async (userId) => {
  try {
    const userRef = doc(firestore, "users", userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      console.warn(`[fetchUser] User not found for ID: ${userId}`);
      return null;
    }

    return { id: userSnap.id, ...userSnap.data() };
  } catch (error) {
    console.error("[fetchUser] Error fetching user details:", error);
    throw new Error("Failed to fetch user details.");
  }
};


/**
 * Updates user details securely.
 * @param {string} userId - The unique ID of the user.
 * @param {Object} updatedData - The fields to update (e.g., { fullName: "John Doe" }).
 * @returns {Promise<void>}
 */
export const updateUser = async (userId, updatedData) => {
  try {
    const userRef = doc(firestore, "users", userId);
    
    // Prevent users from modifying sensitive KYC fields
    const sensitiveFields = ["kycStatus", "govIdURL", "quotaUsed"];
    sensitiveFields.forEach(field => delete updatedData[field]);

    await updateDoc(userRef, updatedData);
    console.log(`[updateUser] User ${userId} updated successfully.`);
  } catch (error) {
    console.error("[updateUser] Error updating user details:", error);
    throw new Error("Failed to update user details.");
  }
};


/**
 * Increments quota usage for a specific user.
 * @param {string} userId - The unique ID of the user.
 * @returns {Promise<void>}
 */
export const incrementQuotaUsage = async (userId) => {
  try {
    const userRef = doc(firestore, "users", userId);
    await updateDoc(userRef, {
      quotaUsed: increment(1),
    });
    console.log(`[incrementQuotaUsage] Quota usage incremented for user: ${userId}`);
  } catch (error) {
    console.error("[incrementQuotaUsage] Error incrementing quota usage:", error);
    throw new Error("Failed to increment quota usage.");
  }
};

/**
 * Resets quota usage for all users (Admin-only action).
 * @returns {Promise<void>}
 */
export const resetQuotaForAllUsers = async () => {
  try {
    const usersRef = collection(firestore, "users");
    const querySnapshot = await getDocs(usersRef);

    const resetPromises = querySnapshot.docs.map((docSnap) =>
      updateDoc(docSnap.ref, { quotaUsed: 0 })
    );

    await Promise.all(resetPromises);
    console.log("[resetQuotaForAllUsers] All user quotas reset successfully.");
  } catch (error) {
    console.error("[resetQuotaForAllUsers] Error resetting user quotas:", error);
    throw new Error("Failed to reset user quotas.");
  }
};


/**
 * Fetches all users subscribed to a specific plan.
 * @param {string} planId - The plan ID (e.g., `starter`, `community`, `unlimited`).
 * @returns {Promise<Array>} An array of user objects.
 */
export const fetchUsersByPlan = async (planId) => {
  try {
    const usersRef = collection(firestore, "users");
    const planQuery = query(usersRef, where("stripePriceid", "==", planId));

    const querySnapshot = await getDocs(planQuery);

    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("[fetchUsersByPlan] Error fetching users by plan:", error);
    throw new Error("Failed to fetch users by plan.");
  }
};


/**
 * Verifies if the user's IP matches their claimed country.
 * @param {string} userId - The unique ID of the user.
 * @param {string} ipCountry - The detected country from IP.
 * @returns {Promise<boolean>} True if matched, false otherwise.
 */
export const verifyUserIP = async (userId, ipCountry) => {
  try {
    const userRef = doc(firestore, "users", userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      console.warn(`[verifyUserIP] User not found: ${userId}`);
      return false;
    }

    const userData = userSnap.data();
    return userData.country === ipCountry;
  } catch (error) {
    console.error("[verifyUserIP] Error verifying user IP:", error);
    return false;
  }
};

/**
 * Fetches user IP and location info.
 */
export const getUserIPInfo = async () => {
  try {
    const response = await fetch("https://ipapi.co/json/");
    const data = await response.json();
    return { ip: data.ip, city: data.city, country: data.country_name };
  } catch (error) {
    console.error("[getUserIPInfo] Error fetching IP:", error);
    return null;
  }
};


/**
 * Creates or updates a user profile document.
 * Automatically sets KYC to 'pending' and tracks submission date.
 * @param {string} userId
 * @param {Object} profileData
 */
export const createUserProfile = async (userId, profileData) => {
  const userRef = doc(firestore, "user_profiles", userId);
  await setDoc(userRef, {
    ...profileData,
    kycStatus: "pending",
    submittedAt: new Date(),
  });
};


// Save User Profile Data & Notify User for KYC Verification
export const submitUserProfile = async (userId, profileData) => {
  try {
    // Reference to user profile
    const userRef = doc(firestore, "user_profiles", userId);
    await setDoc(userRef, {
      ...profileData,
      kycStatus: "pending", // User is required to complete KYC
      submittedAt: new Date(),
    });

    // Reference to user notifications subcollection
    const userNotificationsRef = collection(firestore, "user_profiles", userId, "notifications");

    // Check if a KYC notification already exists to prevent duplicates
    const existingKYCQuery = query(userNotificationsRef, where("type", "==", "kyc_verification_needed"));
    const existingKYCNotifications = await getDocs(existingKYCQuery);

    if (existingKYCNotifications.empty) {
      // Add a notification for KYC validation
      await addDoc(userNotificationsRef, {
        title: "KYC Verification Required",
        message: "Please complete your KYC verification to access full platform features.",
        type: "kyc_verification_needed",
        alertStatus: "warning",
        action: {
          label: "Verify Now",
          url: "/personal/doc-info",
        },
        createdAt: new Date(),
        status: "unread",
      });

      console.log("✅ User profile saved and KYC notification sent.");
    } else {
      console.log("ℹ️ KYC notification already exists, skipping duplicate.");
    }

    return { success: true };
  } catch (error) {
    console.error("❌ Error saving user profile:", error);
    return { success: false, error: error.message };
  }
};

export const fetchUserProfile = async (userId) => {
  if (!userId) {
    throw new Error("Invalid userId: Cannot fetch profile.");
  }

  try {
    console.log("Fetching user profile from user_profiles collection...");
    const userRef = doc(firestore, "user_profiles", userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      console.warn(`[fetchUserProfile] User not found for ID: ${userId}`);
      return null;  // Return null if no profile exists
    }

    const userData = userSnap.data();
    console.log("User profile data retrieved:", userData);

    return { id: userSnap.id, ...userData };
  } catch (error) {
    console.error("[fetchUserProfile] Error fetching user details:", error);
    throw new Error("Failed to fetch user profile.");
  }
};


export const awardFreeTradesAfterVerification = async (userId) => {
  try {
    const userDocRef = doc(firestore, "users", userId);
    const userSnap = await getDoc(userDocRef);

    if (!userSnap.exists()) {
      throw new Error("User not found.");
    }

    const userData = userSnap.data();
    let { freeTrades = 0, receivedVerificationBonus = false, receivedReferralBonus = false } = userData;

    // **Ensure all verifications are completed**
    if (!userData.emailVerified || !userData.phoneVerified || !userData.isProfileUpdated || !userData.kycVerified) {
      console.log(`⚠️ User ${userId} has not completed all verifications. No free trades awarded.`);
      return;
    }

    // **Check if verification bonus was already given**
    if (receivedVerificationBonus) {
      //console.log(`🎯 User ${userId} has already received verification bonus. Skipping.`);
      return;
    }

    // **Award standard free trades (only once)**
    freeTrades += 2;

    // **Check if user was referred and referrer hasn't been awarded yet**
    if (userData.referredByCode && !receivedReferralBonus) {
      const referrerRef = doc(firestore, "users", userData.referredByCode);
      const referrerSnap = await getDoc(referrerRef);

      if (referrerSnap.exists()) {
        const referrerData = referrerSnap.data();
        
        if (!referrerData.referralBonusGiven) {
          // **Increase free trades for referrer only if not already rewarded**
          await updateDoc(referrerRef, {
            freeTrades: (referrerData.freeTrades || 0) + 1,
            referralBonusGiven: true, // ✅ Mark referral bonus as given
          });

          freeTrades += 1; // ✅ Give extra trade to referred user
          receivedReferralBonus = true;
          console.log(`🎉 User ${userId} was referred by ${userData.referredByCode}. Granted extra free trade.`);
        } else {
          console.log(`🔄 Referrer ${userData.referredByCode} already received bonus. Skipping.`);
        }
      }
    }

    // **Update user's free trades & prevent duplicate awards**
    await updateDoc(userDocRef, {
      freeTrades,
      receivedVerificationBonus: true, // ✅ Prevent duplicate free trade awards
      receivedReferralBonus, // ✅ Prevent duplicate referral bonus
    });

    // **Send notifications**
    await sendFreeTradeNotification(userId, freeTrades);
    
    if (receivedReferralBonus) {
      const referrerRef = doc(firestore, "users", userData.referredByCode);
      const referrerSnap = await getDoc(referrerRef);
      if (referrerSnap.exists()) {
        await sendFreeTradeNotification(userData.referredByCode, referrerSnap.data().freeTrades);
      }
    }

    console.log(`✅ User ${userId} completed verification. Awarded ${freeTrades} free trades.`);
  } catch (error) {
    console.error("❌ Error awarding free trades:", error.message);
  }
};


